import CreationToolGradient from './gradient'
import CreationToolWelcomeButton from './welcome-button'

enum CreationTool {
    Backdrop = 'CT_BACKDROP',
    GifMaker = 'CT_GIF_MAKER',
    Upload = 'CT_UPLOAD',
    SuperCut = 'CT_SUPERCUT',
}

namespace CreationTool {
    /**
     * Returns all the tools.
     */
    export function all(): CreationTool[] {
        return [CreationTool.Upload, CreationTool.GifMaker, CreationTool.SuperCut, CreationTool.Backdrop]
    }

    /**
     * Returns the tool's tag added to all uploads.
     */
    export function uploadTag(tool: CreationTool): string {
        switch (tool) {
            case CreationTool.Backdrop:
                return 'giphybackdropmaker'
            case CreationTool.GifMaker:
                return 'giphygifmaker'
            case CreationTool.Upload:
                return 'giphyupload'
            case CreationTool.SuperCut: // TODO: Confirm this should be set as such??
                return 'giphysupercut'
        }
    }

    /**
     * Returns the tool's title.
     */
    export function title(tool: CreationTool): string {
        switch (tool) {
            case CreationTool.Backdrop:
                return 'Backdrop'
            case CreationTool.GifMaker:
                return 'Create'
            case CreationTool.Upload:
                return 'Upload'
            case CreationTool.SuperCut:
                return 'Super Cut'
        }
    }

    /**
     * Returns the tool's description.
     */
    export function description(tool: CreationTool): string {
        switch (tool) {
            case CreationTool.Backdrop:
                return 'Make any video conference far more fun by creating your own Backdrop! Begin by uploading a background or choosing one of our starters.'
            case CreationTool.GifMaker:
                return 'Create GIFs and backdrops and decorate them with captions, filters, stickers, and more.'
            case CreationTool.Upload:
                return 'Upload your collection to share on Facebook, Twitter, Instagram, text message, email, & everywhere else.'
            case CreationTool.SuperCut:
                return 'Automagically create a set of GIFs & Clips from a longer video.'
        }
    }

    /**
     * Returns the tool's gradient colors.
     */
    export function gradient(tool: CreationTool): CreationToolGradient {
        switch (tool) {
            case CreationTool.Backdrop:
                return CreationToolGradient.IndigoPurple
            case CreationTool.SuperCut:
                return CreationToolGradient.BlackBlack
            case CreationTool.GifMaker:
                return CreationToolGradient.PinkIndigo
            case CreationTool.Upload:
                return CreationToolGradient.IndigoBlue
        }
    }

    /**
     * Returns the tool's welcome screen buttons.
     */
    export function welcomeButtons(tool: CreationTool): CreationToolWelcomeButton[] {
        switch (tool) {
            case CreationTool.GifMaker:
                return [
                    CreationToolWelcomeButton.Gif,
                    CreationToolWelcomeButton.Sticker,
                    CreationToolWelcomeButton.Backdrop,
                ]
            case CreationTool.Upload:
                return [
                    CreationToolWelcomeButton.Upload,
                    CreationToolWelcomeButton.UploadSticker,
                    CreationToolWelcomeButton.UploadVideo,
                    CreationToolWelcomeButton.SuperCut,
                ]
            default:
                return []
        }
    }

    /**
     * Returns the tool's welcome screen peeker sticker url.
     */
    export function peekerUrl(tool: CreationTool): string {
        switch (tool) {
            case CreationTool.GifMaker:
                return '/static/img/creation_tools/gif-maker-peeker.gif'
            case CreationTool.Upload:
                return '/static/img/creation_tools/upload-peeker.gif'
            case CreationTool.SuperCut:
                return '/static/img/creation_tools/supercut-peeker.gif'
            default:
                return ''
        }
    }

    /**
     * Returns the tool's welcome screen background video url.
     */
    export function backgroundVideoUrl(tool: CreationTool): string {
        switch (tool) {
            case CreationTool.GifMaker:
                return '/static/img/videos/9rv8tcpftaJcBDLamd.mp4'
            case CreationTool.Upload:
                return '/static/img/videos/forest-still.mp4'
            case CreationTool.SuperCut:
                return '/static/img/videos/gachapon.mp4'
            default:
                return ''
        }
    }

    /**
     * Returns the endpoint prop for suggested tags.
     */
    export function suggestedTagsEndpoint(tool: CreationTool): string {
        switch (tool) {
            case CreationTool.GifMaker:
                return 'gif_maker'
            case CreationTool.Upload:
                return 'upload'
            case CreationTool.SuperCut:
                return 'Super Cut'
            default:
                return ''
        }
    }

    /**
     * Returns whether or not the tool allows inputing multiple files.
     */
    export function supportsMultipleFiles(tool: CreationTool): boolean {
        switch (tool) {
            case CreationTool.Upload:
            case CreationTool.SuperCut:
                return true
            default:
                return false
        }
    }

    /**
     * Returns whether or not the tool requires user to be logged in.
     */
    export function requiresLogin(tool: CreationTool): boolean {
        switch (tool) {
            case CreationTool.GifMaker:
            case CreationTool.Upload:
            case CreationTool.SuperCut:
                return true
            default:
                return false
        }
    }

    /**
     * Returns whether or not the tool requires files to be processed and split into frames for manipulating.
     */
    export function requiresFrameProcessing(tool: CreationTool): boolean {
        switch (tool) {
            case CreationTool.Upload:
            case CreationTool.SuperCut:
                return false
            default:
                return true
        }
    }

    /**
     * Returns the tool's upload creation_location prefix.
     */
    export function creationLocationPrefix(
        tool: CreationTool,
        isBulk: boolean = false,
        isCreated: boolean = false
    ): string {
        switch (tool) {
            case CreationTool.GifMaker:
                return 'desktopweb_gifmaker_create'
            case CreationTool.Upload:
                return `desktopweb_uploadtool_${isBulk ? 'bulk' : ''}${isCreated ? 'create' : 'upload'}`
            case CreationTool.SuperCut:
                return `desktopweb_supercut_${isBulk ? 'bulk' : ''}upload`
            case CreationTool.Backdrop:
                return 'desktopweb_backdrop_create'
        }
    }

    // tools
    export type GACategory =
        | 'GIF maker'
        | 'Upload'
        | 'Bulk Upload'
        | 'Super Cut'
        | 'Backdrop'
        | 'Sticker maker'
        | 'Video'
    /**
     * Returns the tool's GA event category.
     */
    export function gaEventCategory(tool: CreationTool, isBulk: boolean = false): GACategory {
        switch (tool) {
            case CreationTool.GifMaker:
                return 'GIF maker'
            case CreationTool.Upload:
                return isBulk ? `Bulk Upload` : `Upload`
            case CreationTool.SuperCut:
                return `Super Cut`
            case CreationTool.Backdrop:
                return 'Backdrop'
        }
    }
}

export default CreationTool
